import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonGroup, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { BarChart, PieChart } from '@mui/x-charts';
import { LenderRetailActivityLenderRowViewModel } from '../../../api/types';
import { formatAmount, getCountryLabel, getFinancedBarColor, mapFinancedDistribution } from '../../../lib';
import { FinancedDistributionDatum } from '../../../types';
import { getCountryFlag } from './country';

type DistributionView = 'pie' | 'bar';

interface Props {
  rows: LenderRetailActivityLenderRowViewModel[];
  loading?: boolean;
}

const CHART_HEIGHT = 280;

export const FinancedDistribution = ({ rows, loading }: Props) => {
  const distribution = useMemo(() => mapFinancedDistribution(rows), [rows]);

  const legendGroups = useMemo(() => {
    const groups = new Map<string, FinancedDistributionDatum[]>();

    distribution.forEach(datum => {
      const list = groups.get(datum.country) ?? [];
      list.push(datum);
      groups.set(datum.country, list);
    });

    return Array.from(groups.entries());
  }, [distribution]);

  const pieData = distribution.map(({ id, label, value, color }) => ({ id, label, value, color }));
  const total = distribution.reduce((sum, datum) => sum + datum.value, 0);

  const [view, setView] = useState<DistributionView>('pie');

  const share = (value: number) => (total ? (value / total) * 100 : 0);
  const axisMax = Math.max(
    Math.ceil(distribution.reduce((max, datum) => Math.max(max, share(datum.value)), 0) / 20) * 20,
    20
  );

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2" component="span">
        Financed app distribution ($)
      </Typography>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <ButtonGroup size="small" color="primary" sx={{ mb: 2 }}>
          <Button variant={view === 'pie' ? 'contained' : 'outlined'} onClick={() => setView('pie')}>
            Pie chart
          </Button>
          <Button variant={view === 'bar' ? 'contained' : 'outlined'} onClick={() => setView('bar')}>
            Bar chart
          </Button>
        </ButtonGroup>

        {loading ? (
          <Skeleton variant="rounded" height={CHART_HEIGHT} sx={{ mt: 1 }} />
        ) : !distribution.length ? (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            No data
          </Typography>
        ) : view === 'pie' ? (
          <Box
            sx={{
              mt: 1,
              gap: 3,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'flex-start',
            }}
          >
            <Stack gap={1.5} sx={{ minWidth: 200 }}>
              {legendGroups.map(([country, items]) => {
                const flag = getCountryFlag(country);

                return (
                  <Stack key={country} gap={0.75}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      {flag && <Box component={flag} sx={{ width: 16, height: 16 }} />}
                      <Typography variant="subtitle2" component="span">
                        {getCountryLabel(country)}
                      </Typography>
                    </Stack>

                    <Stack gap={0.5}>
                      {items.map(item => (
                        <Tooltip key={item.id} title={formatAmount(item.value)}>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Box
                              sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: item.color, flexShrink: 0 }}
                            />
                            <Typography variant="body2">
                              {item.label} — {((item.value / total) * 100).toFixed(1)}%
                            </Typography>
                          </Stack>
                        </Tooltip>
                      ))}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', width: CHART_HEIGHT, height: CHART_HEIGHT }}>
                <PieChart
                  width={CHART_HEIGHT}
                  height={CHART_HEIGHT}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  series={[
                    {
                      data: pieData,
                      innerRadius: 70,
                      outerRadius: 120,
                      paddingAngle: 1,
                      cornerRadius: 2,
                      valueFormatter: item => formatAmount(item.value),
                    },
                  ]}
                  slotProps={{ legend: { hidden: true } }}
                />

                <Box
                  sx={{
                    inset: 0,
                    display: 'flex',
                    position: 'absolute',
                    alignItems: 'center',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography variant="subtitle2" component="span">
                    Total:
                  </Typography>
                  <Typography variant="subtitle1" /* fontWeight={600} */>{formatAmount(total)}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Stack gap={3} sx={{ mt: 1 }}>
            {legendGroups.map(([country, items]) => {
              const flag = getCountryFlag(country);

              return (
                <Box key={country}>
                  <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                    {flag && <Box component={flag} sx={{ width: 16, height: 16 }} />}
                    <Typography variant="subtitle2">{getCountryLabel(country)}</Typography>
                  </Stack>

                  <BarChart
                    layout="horizontal"
                    height={Math.max(items.length * 40 + 40, 120)}
                    margin={{ left: 160, top: 10, right: 24, bottom: 24 }}
                    yAxis={[{ scaleType: 'band', data: items.map(item => item.label) }]}
                    xAxis={[{ min: 0, max: axisMax, tickMinStep: 20, valueFormatter: value => `${value}%` }]}
                    series={[
                      {
                        data: items.map(item => share(item.value)),
                        color: getFinancedBarColor(country),
                        valueFormatter: value => (value == null ? '' : `${value.toFixed(1)}%`),
                      },
                    ]}
                    barLabel={item => (item.value == null ? '' : `${item.value.toFixed(1)}%`)}
                    slotProps={{ legend: { hidden: true } }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};
